import { FC, ReactNode, useCallback, useEffect, useMemo, useState, useRef, PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';

import { SnackbarRenderer } from '@hh.ru/magritte-ui-snackbar/SnackbarRenderer';
import { SnackbarContext, SnackbarContextProps } from '@hh.ru/magritte-ui-snackbar/context';
import { SnackbarProps, SnackbarState, SnackbarTransitionName } from '@hh.ru/magritte-ui-snackbar/types';

import styles from './snackbar.less';

export const SnackbarContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const animationTimeoutRef = useRef({
        [SnackbarTransitionName.Show]: 0,
        [SnackbarTransitionName.Hide]: 0,
        [SnackbarTransitionName.Autohide]: 0,
        [SnackbarTransitionName.Replace]: 0,
        [SnackbarTransitionName.RemoveImmediately]: 0,
    });
    const containerRefs = useRef<{ [id: string]: HTMLElement }>({});
    const isShownRef = useRef<{ [uid: string]: boolean }>({});
    const [state, setState] = useState<{ [uid: string]: SnackbarState }>({});

    const animateOut: SnackbarContextProps['animateOut'] = useCallback(
        (uid, transition) => {
            setState((prevState) => {
                if (uid in prevState) {
                    const prevTransition = prevState[uid].transition;

                    const isCurrentlyVisible =
                        prevTransition === SnackbarTransitionName.Show ||
                        prevTransition === SnackbarTransitionName.Replace;

                    const willBeHidden =
                        transition === SnackbarTransitionName.Hide || transition === SnackbarTransitionName.Autohide;

                    if (
                        transition === SnackbarTransitionName.RemoveImmediately ||
                        (willBeHidden && !isCurrentlyVisible)
                    ) {
                        return Object.fromEntries(Object.entries(prevState).filter(([key]) => key !== uid));
                    }

                    return { ...prevState, [uid]: { ...prevState[uid], transition } };
                }

                return prevState;
            });
        },
        [setState]
    );

    const animateIn: SnackbarContextProps['animateIn'] = useCallback(
        (uid: string, props: SnackbarProps) => {
            setState((prevState) => {
                if (uid in prevState) {
                    // не даем менять некоторые пропсы у существующего снекбара
                    const { autohideTime, containerId, showTimer, ..._props } = props;
                    return { ...prevState, [uid]: { ...prevState[uid], ..._props } as SnackbarState };
                }

                const _props: Pick<SnackbarState, 'animateOut' | 'isShown' | 'onShown' | 'transition'> = {
                    animateOut: (transition) => animateOut(uid, transition),
                    isShown: () => !!isShownRef.current[uid],
                    onShown: () => (isShownRef.current[uid] = true),
                    transition: SnackbarTransitionName[Object.keys(prevState).length > 0 ? 'Replace' : 'Show'],
                };

                return Object.keys(prevState).reduce(
                    (accumulator, key) => {
                        const value = { ...prevState[key], transition: SnackbarTransitionName.Hide };
                        return key === uid ? accumulator : { ...accumulator, [key]: value };
                    },
                    { [uid]: { ..._props, ...props } as SnackbarState }
                );
            });
        },
        [animateOut, setState]
    );

    const renderFunc = useCallback(
        (uid: string): ReactNode => {
            const containerId = state[uid].containerId;
            const container = containerId ? containerRefs.current[containerId] : document.body;

            if (!container) {
                return null;
            }

            return createPortal(
                <SnackbarRenderer {...state[uid]} timeout={animationTimeoutRef.current[state[uid].transition]} />,
                container,
                uid
            );
        },
        [state]
    );

    const setContainerRef: SnackbarContextProps['setContainerRef'] = useCallback((containerId, instance) => {
        if (!instance) {
            return;
        }

        containerRefs.current[containerId] = instance;
    }, []);

    useEffect(() => {
        const animationTimeoutElement = document.createElement('div');
        animationTimeoutElement.classList.add(styles['snackbar-animation-timeout']);
        document.body.appendChild(animationTimeoutElement);
        const style = window.getComputedStyle(animationTimeoutElement);

        for (const transition of Object.values(SnackbarTransitionName)) {
            const animationTimeout = parseInt(style.getPropertyValue(`--${transition}-animation-duration`), 10);
            if (Number.isInteger(animationTimeout)) {
                animationTimeoutRef.current[transition] = animationTimeout;
            }
        }

        document.body.removeChild(animationTimeoutElement);
    }, []);

    const value = useMemo<SnackbarContextProps>(() => {
        return {
            animateIn,
            animateOut,
            isShown: (uid) => isShownRef.current[uid],
            setContainerRef,
        };
    }, [animateIn, animateOut, setContainerRef]);

    return (
        <SnackbarContext.Provider value={value}>
            {Object.keys(state).map(renderFunc)}
            {children}
        </SnackbarContext.Provider>
    );
};
